import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnChanges } from '@angular/core';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { CampaignWorkflow } from '../../../models/campaign.models';
import { todayLocalDateString, toLocalDateString } from '../../../shared/date.util';
import { stageColor } from '../board/stage-colors';

/** Charts project the saved Campaign workflow summary, never the currently loaded table page. */
@Component({
  selector: 'app-campaign-progress-charts',
  standalone: true,
  imports: [CommonModule, NgChartsModule],
  templateUrl: './campaign-progress-charts.component.html',
  styleUrls: ['./campaign-progress-charts.component.css'],
})
export class CampaignProgressChartsComponent implements OnChanges {
  @Input() workflow: CampaignWorkflow | null = null;
  @Input() loading = false;
  @Input() step = '3';
  readiness: ChartData<'doughnut'> = { labels: [], datasets: [] };
  distribution: ChartData<'bar'> = { labels: [], datasets: [] };
  barOptions: ChartOptions<'bar'> = {};
  donutOptions: ChartOptions<'doughnut'> = {};
  title = '';
  caption = '';
  available = false;
  chartTotal = 0;
  accessibleSummary = '';
  readinessSummary = '';
  readinessPercent = 0;
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  ngOnChanges(): void {
    const style = getComputedStyle(this.document.documentElement);
    const color = (name: string) => style.getPropertyValue(`--br-color-${name}`).trim();
    const muted = color('muted'), text = color('text');
    this.barOptions = {
      responsive: true, maintainAspectRatio: false, animation: false, indexAxis: 'y',
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0, color: muted }, grid: { color: color('border-soft') } },
        y: { ticks: { color: text }, grid: { display: false } },
      },
    };
    this.donutOptions = { responsive: true, maintainAspectRatio: false, animation: false, cutout: '72%',
      plugins: { legend: { position: 'bottom', labels: { color: text, usePointStyle: true, boxWidth: 8 } } } };
    const workflow = this.workflow;
    if (!workflow) { this.available = false; return; }
    const remaining = Math.max(0, workflow.targetCount - workflow.arrangedCount - workflow.unavailableCount);
    this.readinessPercent = workflow.targetCount ? Math.round(100 * workflow.arrangedCount / workflow.targetCount) : 0;
    this.readinessSummary = `Ready: ${workflow.arrangedCount}. Needs planning: ${remaining}. Unavailable: ${workflow.unavailableCount}.`;
    this.readiness = {
      labels: ['Ready', 'Needs planning', 'Unavailable'],
      datasets: [{ data: [workflow.arrangedCount, remaining, workflow.unavailableCount],
        backgroundColor: [color('success'), color('primary-soft'), color('muted')], borderWidth: 0 }],
    };
    let labels: string[], values: number[], colors: string[];
    if (this.step === '4') {
      this.title = 'Follow-up workload';
      this.caption = 'Open Leads by saved next-action date.';
      this.available = workflow.followUpDates != null;
      labels = ['Overdue', 'Due today', 'Upcoming', 'No due date']; values = [0, 0, 0, 0];
      const today = todayLocalDateString();
      for (const group of workflow.followUpDates ?? []) {
        const date = group.dueAt ? new Date(group.dueAt) : null;
        const day = date && !Number.isNaN(date.getTime()) ? toLocalDateString(date) : null;
        values[day == null ? 3 : day < today ? 0 : day === today ? 1 : 2] += group.count;
      }
      colors = [color('danger'), color('primary'), color('success'), color('muted')];
    } else if (this.step === '3') {
      this.title = 'Planned actions';
      this.caption = 'One current next action per Lead.';
      this.available = workflow.actionCounts != null;
      labels = (workflow.actionCounts ?? []).map(group => group.label);
      values = (workflow.actionCounts ?? []).map(group => group.count);
      colors = labels.map(() => color('primary'));
    } else {
      this.title = 'Current Lead stages';
      this.caption = "Current stages of this campaign's Leads.";
      this.available = workflow.stageCounts != null;
      labels = (workflow.stageCounts ?? []).map(group => group.label);
      values = (workflow.stageCounts ?? []).map(group => group.count);
      colors = (workflow.stageCounts ?? []).map(group => stageColor(group.key));
    }
    this.chartTotal = values.reduce((sum, value) => sum + value, 0);
    this.accessibleSummary = labels.map((label, index) => `${label}: ${values[index]}`).join('. ');
    this.distribution = { labels, datasets: [{ label: 'Leads', data: values, backgroundColor: colors, borderRadius: 4, maxBarThickness: 28 }] };
  }
}
